import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, ChevronDown, Layers3, UserPlus, UserRound, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { openAssignPersonnel } from './AssignPersonnelModal';
import ExerciseTimelinePreview, { AssignedRoleChip, isScenarioRecommendationAssignment } from './ExerciseTimelinePreview';
import { CommanServices } from '@/services/commanServices';
import { ApiEndPoint } from '@/services/apiUttiLities';
import socketService from '@/services/socketService';
import ScenarioCandidateSourceChips, { normalizeScenarioCandidateSources } from './ScenarioCandidateSourceChips';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { mergeStaffingReviewReadiness } from './liveIncidentReadiness';
import PersonnelAvailabilityFilters from './PersonnelAvailabilityFilters';
import PersonnelAvailabilitySummary from './PersonnelAvailabilitySummary';
import {
  currentRoleAssignmentEntries,
  DEFAULT_PERSONNEL_AVAILABILITY_FILTERS,
  dutyRank,
  passesPersonnelAvailabilityFilters,
  personnelAssignmentWarnings,
  readinessRank,
  usableReadinessDistance,
} from './personnelAvailability';

const groupLabel = (card) => card.group === 'response_action'
  ? `Response Actions${card.title ? ` · ${card.title}` : ''}`
  : card.group === 'acknowledgment'
    ? 'Verifications'
    : card.group === 'notification'
      ? 'Notifications'
      : card.title || 'Response card';

const keepPreviousWhenEqual = (previous, next) => (
  JSON.stringify(previous) === JSON.stringify(next) ? previous : next
);

const loadGeneralDuty = () => axios
  .get(`${ApiEndPoint}/api/duty-status`, { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } })
  .then((response) => response.data?.data?.users || {});

export default function QuickAssignModal({ incident, items = [], isOpen, onClose = () => {}, onChanged, embedded = false, onlineUserIds = new Set(), presenceByUserId = {} }) {
  const [review, setReview] = useState({ cards: [], gaps: 0, unfillable_gaps: 0 });
  const [duty, setDuty] = useState({});
  const [readinessLoadState, setReadinessLoadState] = useState('loading');
  const [confirmation, setConfirmation] = useState(null);
  const [expandedCandidates, setExpandedCandidates] = useState({});
  const [collapsedCandidateGroups, setCollapsedCandidateGroups] = useState({});
  const [rankingInfoKey, setRankingInfoKey] = useState('');
  const [availabilityFilters, setAvailabilityFilters] = useState(() => ({ ...DEFAULT_PERSONNEL_AVAILABILITY_FILTERS }));
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');
  const staffingRequestRef = useRef(0);
  const staffingRefreshQueuedRef = useRef(false);
  const locationRefreshTimerRef = useRef(null);
  const reviewRef = useRef(review);

  useEffect(() => { reviewRef.current = review; }, [review]);

  const itemsById = useMemo(() => new Map((items || []).map((item) => [String(item.id), item])), [items]);
  const assignmentLoadByUser = useMemo(() => {
    const load = new Map();
    (items || []).forEach((item) => {
      if (item?.is_active === false) return;
      currentRoleAssignmentEntries(item).forEach(([, assignments]) => {
        (assignments || []).forEach((assignment) => {
          if (!assignment?.id) return;
          const userId = String(assignment.id);
          load.set(userId, Number(load.get(userId) || 0) + 1);
        });
      });
    });
    return load;
  }, [items]);

  const loadDuty = useCallback(async () => {
    if (!incident?.id) return;
    try {
      const next = await loadGeneralDuty();
      setDuty((previous) => keepPreviousWhenEqual(previous, next));
    } catch (loadError) {
      setDuty({});
    }
  }, [incident?.id]);

  const loadStaffing = useCallback(async ({ silent = false } = {}) => {
    if (!incident?.id) return;
    const requestId = staffingRequestRef.current + 1;
    staffingRequestRef.current = requestId;
    if (!silent) setLoading(true);
    try {
      const response = await CommanServices.getIncidentStaffingReview(incident.id);
      if (response?.status === false) throw new Error(response.message || 'Unable to load staffing');
      if (requestId !== staffingRequestRef.current) return;
      const next = response?.data || { cards: [], gaps: 0, unfillable_gaps: 0 };
      setReview((previous) => keepPreviousWhenEqual(previous, next));
      setReadinessLoadState('loaded');
      setError('');
    } catch (loadError) {
      if (requestId !== staffingRequestRef.current) return;
      setReadinessLoadState('unavailable');
      setError(loadError?.message || 'Unable to load staffing');
    } finally {
      if (requestId === staffingRequestRef.current) setLoading(false);
    }
  }, [incident?.id]);

  const load = useCallback(async () => {
    setConfirmation(null);
    setReadinessLoadState('loading');
    await Promise.all([loadStaffing(), loadDuty()]);
  }, [loadDuty, loadStaffing]);

  const queueLiveStaffingRefresh = useCallback(() => {
    if (staffingRefreshQueuedRef.current) return;
    staffingRefreshQueuedRef.current = true;
    Promise.resolve().then(() => {
      staffingRefreshQueuedRef.current = false;
      loadStaffing({ silent: true });
    });
  }, [loadStaffing]);

  const queueLocationStaffingRefresh = useCallback(() => {
    if (locationRefreshTimerRef.current) window.clearTimeout(locationRefreshTimerRef.current);
    locationRefreshTimerRef.current = window.setTimeout(() => {
      locationRefreshTimerRef.current = null;
      loadStaffing({ silent: true });
    }, 1000);
  }, [loadStaffing]);

  useEffect(() => {
    if (!isOpen) return undefined;
    setConfirmation(null);
    setExpandedCandidates({});
    setCollapsedCandidateGroups({});
    setRankingInfoKey('');
    setAvailabilityFilters({ ...DEFAULT_PERSONNEL_AVAILABILITY_FILTERS });
    load();
    const offReadiness = socketService.on('staffing_readiness_updated', (event) => {
      if (String(event?.incident_id) !== String(incident?.id)) return;
      if (event?.user_id) {
        setReview((previous) => mergeStaffingReviewReadiness(previous, event));
        setReadinessLoadState('loaded');
      }
      queueLiveStaffingRefresh();
    });
    const offDuty = socketService.on('user_duty_status_updated', (event) => {
      const nextStatus = event?.duty_status || event?.status;
      if (!event?.user_id || !nextStatus) return;
      setDuty((previous) => ({
        ...previous,
        [String(event.user_id)]: {
          ...(previous[String(event.user_id)] || {}),
          status: nextStatus,
          responded_at: event.responded_at || null,
        },
      }));
      setConfirmation(null);
      queueLiveStaffingRefresh();
    });
    const sync = (event) => {
      if (String(event?.detail?.incident_id || '') === String(incident?.id)) queueLiveStaffingRefresh();
    };
    const refresh = (event) => {
      const changedIncidentId = event?.detail?.incident_id;
      if (changedIncidentId && String(changedIncidentId) !== String(incident?.id)) return;
      queueLiveStaffingRefresh();
    };
    const refreshForLocation = (event) => {
      const userId = String(event?.user_id || '');
      if (!userId) return;
      const hasCandidate = (reviewRef.current?.cards || []).some((card) => (card.roles || []).some((role) => (
        (role.candidates || []).some((candidate) => String(candidate?.user_id || '') === userId)
      )));
      if (hasCandidate) queueLocationStaffingRefresh();
    };
    const reconnect = () => {
      queueLiveStaffingRefresh();
      loadDuty();
    };
    window.addEventListener('incidentRealtimeSync', sync);
    window.addEventListener('timelineRefresh', refresh);
    const offLocation = socketService.on('team_location_updated', refreshForLocation);
    const offLocationOffline = socketService.on('team_location_offline', refreshForLocation);
    const offReconnect = socketService.on('connect', reconnect);
    return () => {
      staffingRequestRef.current += 1;
      staffingRefreshQueuedRef.current = false;
      if (locationRefreshTimerRef.current) window.clearTimeout(locationRefreshTimerRef.current);
      locationRefreshTimerRef.current = null;
      offReadiness?.();
      offDuty?.();
      offLocation?.();
      offLocationOffline?.();
      offReconnect?.();
      window.removeEventListener('incidentRealtimeSync', sync);
      window.removeEventListener('timelineRefresh', refresh);
    };
  }, [isOpen, incident?.id, load, loadDuty, queueLiveStaffingRefresh, queueLocationStaffingRefresh]);

  const accept = async (payload, key, successMessage) => {
    if (busyKey) return;
    setBusyKey(key);
    setError('');
    try {
      const response = await CommanServices.acceptIncidentStaffing(incident.id, payload);
      if (response?.status === false) throw new Error(response.message || 'Unable to accept staffing');
      toast.success(successMessage);
      await onChanged?.(true);
      if (payload.activate) {
        onClose();
        window.dispatchEvent(new Event('timelineRefresh'));
        window.dispatchEvent(new Event('dashboardRefresh'));
      } else {
        await load();
      }
    } catch (acceptError) {
      setError(acceptError?.message || 'Unable to accept staffing');
    } finally {
      setBusyKey('');
    }
  };

  const dutyInfoFor = (userId) => duty[String(userId)] || {};
  const readinessInfoFor = (candidate) => candidate || null;
  const presenceInfoFor = (userId) => presenceByUserId?.[String(userId)]
    || (onlineUserIds.has(String(userId)) ? { online: true } : null);
  const passesAvailabilityFilter = (candidate) => passesPersonnelAvailabilityFilters({
    presence: presenceInfoFor(candidate?.user_id),
    dutyInfo: dutyInfoFor(candidate?.user_id),
    readinessInfo: readinessInfoFor(candidate),
    readinessLoadState,
    filters: availabilityFilters,
  });
  const compareCandidateAvailability = (a, b) => (
    dutyRank(dutyInfoFor(a.user_id)) - dutyRank(dutyInfoFor(b.user_id))
    || readinessRank(readinessInfoFor(a), readinessLoadState) - readinessRank(readinessInfoFor(b), readinessLoadState)
    || usableReadinessDistance(readinessInfoFor(a)) - usableReadinessDistance(readinessInfoFor(b))
    || Number(a.default_order ?? Number.MAX_SAFE_INTEGER) - Number(b.default_order ?? Number.MAX_SAFE_INTEGER)
  );

  const warningsFor = (candidate) => {
    return personnelAssignmentWarnings(
      dutyInfoFor(candidate?.user_id),
      readinessInfoFor(candidate),
      readinessLoadState,
    );
  };

  const requestAccept = (payload, key, successMessage, candidates = []) => {
    if (busyKey) return;
    const unsafe = candidates.map((candidate) => ({
      candidate,
      warnings: warningsFor(candidate),
    })).filter((entry) => entry.warnings.length > 0);
    if (!unsafe.length) {
      accept(payload, key, successMessage);
      return;
    }
    setConfirmation({ payload, key, successMessage, unsafe });
  };

  const confirmAccept = () => {
    if (!confirmation || busyKey) return;
    const pending = confirmation;
    setConfirmation(null);
    accept(pending.payload, pending.key, pending.successMessage);
  };

  if (!isOpen) return null;
  const fullyStaffed = Number(review.gaps || 0) === 0;
  const staffingCards = (review.cards || []).filter((card) => card.roles.some((role) => Number(role.gap || 0) > 0));

  const safeRecommendationUsers = new Set();
  const safeRecommendations = staffingCards.flatMap((card) => (card.roles || []).flatMap((role) => {
    const candidatesById = new Map((role.candidates || []).map((candidate) => [String(candidate.user_id), candidate]));
    return (role.recommended_user_ids || []).map((userId) => ({
      card,
      role,
      candidate: candidatesById.get(String(userId)),
    })).filter((entry) => {
      const userId = String(entry.candidate?.user_id || '');
      if (!entry.candidate || warningsFor(entry.candidate).length > 0 || safeRecommendationUsers.has(userId)) return false;
      safeRecommendationUsers.add(userId);
      return true;
    });
  }));

  const applySafeRecommendations = async () => {
    if (busyKey || confirmation) return;
    if (!safeRecommendations.length) {
      toast.info('No on-duty, incident-ready recommendations are available to apply automatically');
      return;
    }
    setBusyKey('safe-recommendations');
    setError('');
    let appliedCount = 0;
    try {
      for (const entry of safeRecommendations) {
        const response = await CommanServices.acceptIncidentStaffing(incident.id, {
          card_id: entry.card.id,
          role: entry.role.role,
          user_id: entry.candidate.user_id,
        });
        if (response?.status === false) throw new Error(response.message || 'Unable to apply safe recommendations');
        appliedCount += 1;
      }
      toast.success(`${safeRecommendations.length} safe recommendation${safeRecommendations.length === 1 ? '' : 's'} applied`);
    } catch (applyError) {
      setError(`${appliedCount ? `${appliedCount} recommendation${appliedCount === 1 ? '' : 's'} applied before the remaining update failed. ` : ''}${applyError?.message || 'Unable to apply safe recommendations'}`);
    } finally {
      if (appliedCount > 0) {
        try {
          await onChanged?.(true);
          await load();
        } catch (refreshError) {
          setError((current) => current || 'Staffing was updated, but the latest view could not be refreshed.');
        }
      }
      setBusyKey('');
    }
  };

  const renderStaffingCard = (card, sourceTimelineItem = null, attachedToTimeline = false) => {
    const rawTimelineItem = sourceTimelineItem || itemsById.get(String(card.id));
    const validAssignments = Object.fromEntries((card.roles || []).map((role) => [role.role, role.assignments || []]));
    const timelineItem = rawTimelineItem ? {
      ...rawTimelineItem,
      meta: { ...(rawTimelineItem.meta || {}), roleAssignments: validAssignments },
    } : null;
    const cardGap = (card.roles || []).reduce((sum, role) => sum + Number(role.gap || 0), 0);
    const visibleRoles = (card.roles || []).filter((role) => {
      if (Number(role.gap || 0) > 0) return true;
      if (!attachedToTimeline || !(role.assignments || []).some(isScenarioRecommendationAssignment)) return false;
      return (role.candidates || []).some((candidate) => !candidate.assigned);
    });
    if (!visibleRoles.length) return null;

    return (
      <section
        key={card.id}
        className={attachedToTimeline ? 'bg-slate-950/20 px-3 py-2' : 'rounded-xl border border-amber-800/70 bg-slate-800/60 p-3'}
        data-testid={`quick-assign-card-${card.id}`}
      >
        {!attachedToTimeline && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-bold text-slate-100">{groupLabel(card)}</p>
              <p className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-400">{cardGap} staffing gap{cardGap === 1 ? '' : 's'}</p>
            </div>
          </div>
        )}
        <div className={`${attachedToTimeline ? 'divide-y divide-slate-700/70' : 'mt-3 space-y-2'}`}>
          {visibleRoles.map((role) => {
            const recommendationIds = new Set((role.recommended_user_ids || []).map(String));
            const sorted = [...(role.candidates || [])].sort(compareCandidateAvailability);
            const filteredSorted = sorted.filter(passesAvailabilityFilter);
            const recommended = filteredSorted.filter((candidate) => recommendationIds.has(String(candidate.user_id)));
            const alternatives = filteredSorted.filter((candidate) => !recommendationIds.has(String(candidate.user_id)) && !candidate.assigned);
            const expandKey = `${card.id}:${role.role}`;
            // The numbered embedded list follows the actual availability comparator.
            // recommended_user_ids is the server's auto-fill subset, not a second rank.
            const hasScenarioAutoAssignment = (role.assignments || []).some(isScenarioRecommendationAssignment);
            const isFullyStaffedAutoRole = attachedToTimeline && Number(role.gap || 0) === 0 && hasScenarioAutoAssignment;
            const compactCandidates = sorted
              .map((candidate, index) => ({ candidate, rank: index + 1 }))
              .filter(({ candidate }) => passesAvailabilityFilter(candidate))
              .filter(({ candidate }) => isFullyStaffedAutoRole || !candidate.assigned);
            const compactGroupCollapsed = !!collapsedCandidateGroups[expandKey];
            const showCompactCandidates = !isFullyStaffedAutoRole || !!expandedCandidates[expandKey];
            const compactLimit = 1;
            const collapsedCandidates = attachedToTimeline ? recommended.slice(0, compactLimit) : recommended;
            const hiddenCandidates = attachedToTimeline ? [...recommended.slice(compactLimit), ...alternatives] : alternatives;
            const visibleCandidates = expandedCandidates[expandKey] ? [...recommended, ...alternatives] : collapsedCandidates;
            // Timeline-embedded view: a role with no assignments and no saved
            // candidates has nothing to show — skip it entirely so no empty
            // divider lines appear (the card's gap chip already covers it).
            if (attachedToTimeline && !(role.assignments || []).length && compactCandidates.length === 0) return null;
            return (
              <div key={role.role} className={attachedToTimeline ? 'py-2 first:pt-0 last:pb-0' : 'rounded-lg border border-slate-700 bg-slate-950/50 p-3'}>
                {!attachedToTimeline && (
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-xs font-bold text-slate-100">{role.role}</p>
                      <p className="mt-0.5 text-[10px] text-slate-400">{role.assigned}/{role.required} staffed</p>
                    </div>
                    {timelineItem && (
                    <button
                      type="button"
                      onClick={() => openAssignPersonnel({ itemId: card.id, role: role.role, variant: 'dark', item: timelineItem })}
                      className="inline-flex items-center gap-1 rounded-md border border-dashed border-cyan-700 px-2.5 py-1.5 text-[10px] font-bold text-cyan-300 hover:bg-cyan-950/50"
                      data-testid={`choose-other-${card.id}-${role.role}`}
                    >
                      <UserPlus className="h-3 w-3" /> Choose someone else
                    </button>
                    )}
                  </div>
                )}
                {(role.assignments || []).length > 0 && (
                  <div className={`${attachedToTimeline ? 'no-scrollbar flex flex-nowrap items-center gap-1 overflow-x-auto whitespace-nowrap' : 'mt-2 flex flex-wrap items-center gap-1.5'}`}>
                    {attachedToTimeline ? (
                      <AssignedRoleChip
                        role={role.role}
                        names={(role.assignments || []).map((entry) => entry.name || entry.email)}
                        dark
                        testId={`assigned-role-chip-${card.id}-${role.role}`}
                      />
                    ) : (
                      <p className="text-[10px] text-emerald-300">Assigned: {(role.assignments || []).map((entry) => entry.name || entry.email).join(', ')}</p>
                    )}
                    {attachedToTimeline && hasScenarioAutoAssignment && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-800 bg-emerald-950/50 px-1.5 py-0.5 text-[9px] font-semibold text-emerald-300" data-testid={`partial-auto-assigned-${card.id}-${role.role}`}>
                        <CheckCircle2 className="h-2.5 w-2.5" /> Auto-assigned
                      </span>
                    )}
                    {isFullyStaffedAutoRole && compactCandidates.length > 0 && (
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setExpandedCandidates((current) => ({ ...current, [expandKey]: !current[expandKey] }));
                        }}
                        className="inline-flex items-center gap-1 text-[9px] font-bold text-cyan-300 hover:text-cyan-200"
                        aria-expanded={!!expandedCandidates[expandKey]}
                        data-testid={`fully-staffed-alternatives-${card.id}-${role.role}`}
                      >
                        {expandedCandidates[expandKey] ? 'Hide alternatives' : 'See alternatives'}
                        <ChevronDown className={`h-3 w-3 transition-transform ${expandedCandidates[expandKey] ? 'rotate-180' : ''}`} />
                      </button>
                    )}
                  </div>
                )}
                {attachedToTimeline && showCompactCandidates && compactCandidates.length > 0 ? (
                  <div
                    className="mt-2 overflow-hidden rounded-lg border border-slate-700/80 bg-slate-900/55"
                    data-testid={`candidate-group-${card.id}-${role.role}`}
                  >
                    <div className="flex items-center justify-between gap-2 border-b border-slate-700/70 bg-slate-900/80 px-2.5 py-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setRankingInfoKey((current) => current === expandKey ? '' : expandKey);
                          }}
                          className="rounded-full border border-violet-800 bg-violet-950/40 px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-[0.12em] text-violet-300 hover:border-violet-600 hover:bg-violet-950/70"
                          aria-expanded={rankingInfoKey === expandKey}
                          data-testid={`candidate-ranking-info-toggle-${card.id}-${role.role}`}
                        >
                          Recommended
                        </button>
                        <span className="rounded-full bg-slate-800 px-1.5 py-0.5 text-[9px] font-bold text-slate-400">{compactCandidates.length}</span>
                      </div>
                      {!isFullyStaffedAutoRole && (
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setCollapsedCandidateGroups((current) => ({ ...current, [expandKey]: !current[expandKey] }));
                          }}
                          className="inline-flex items-center gap-1 text-[9px] font-bold text-cyan-300 hover:text-cyan-200"
                          aria-expanded={!compactGroupCollapsed}
                          data-testid={`candidate-group-toggle-${card.id}-${role.role}`}
                        >
                          {compactGroupCollapsed ? 'Show' : 'Hide'}
                          <ChevronDown className={`h-3 w-3 transition-transform ${compactGroupCollapsed ? '' : 'rotate-180'}`} />
                        </button>
                      )}
                    </div>
                    {!compactGroupCollapsed && (
                      <>
                        {rankingInfoKey === expandKey && (
                          <div className="border-b border-violet-800/60 bg-violet-950/25 px-2.5 py-2 text-[10px] leading-relaxed text-slate-300" data-testid={`candidate-ranking-explanation-${card.id}-${role.role}`}>
                            <strong className="text-violet-200">Why this ranking?</strong>{' '}
                            From this card’s Default Candidates. Ranked by duty, incident readiness, usable live ETA, then saved order.
                          </div>
                        )}
                        <div className="divide-y divide-slate-700/70">
                          {compactCandidates.map(({ candidate, rank }) => {
                            const candidateDuty = dutyInfoFor(candidate.user_id);
                            const workload = Number(assignmentLoadByUser.get(String(candidate.user_id)) || 0);
                            const candidateSources = normalizeScenarioCandidateSources(candidate);
                            const visibleSources = candidateSources.filter((source) => source.type !== 'user');
                            const isDirectOnly = candidateSources.length > 0 && visibleSources.length === 0;
                            return (
                              <div
                                key={candidate.user_id}
                                className="no-scrollbar flex max-w-full min-w-0 flex-nowrap items-center gap-2 overflow-x-auto whitespace-nowrap px-2.5 py-2 text-[10px]"
                                data-testid={`compact-candidate-${card.id}-${role.role}-${candidate.user_id}`}
                              >
                                <span className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[9px] font-black tabular-nums ${candidate.assigned ? 'border-emerald-600 bg-emerald-500 text-slate-950 ring-2 ring-emerald-400/30' : 'border-slate-600 bg-slate-800 text-slate-200'}`} aria-label={candidate.assigned ? `Rank ${rank}, selected assignment` : `Rank ${rank}`}>{rank}</span>
                                <span
                                  className="flex min-w-[90px] max-w-[145px] shrink-0 flex-col items-start gap-0.5 leading-tight"
                                  data-testid={`candidate-identity-${card.id}-${role.role}-${candidate.user_id}`}
                                >
                                  <span className="max-w-full truncate font-semibold text-slate-100">{candidate.user_name}</span>
                                  {workload > 0 && (
                                    <span
                                      className="inline-flex max-w-full items-center rounded-full border border-amber-800/80 bg-amber-950/45 px-1.5 py-0.5 text-[8px] font-bold text-amber-300"
                                      title={`${workload} active assignment${workload === 1 ? '' : 's'}`}
                                      data-testid={`candidate-workload-${card.id}-${role.role}-${candidate.user_id}`}
                                    >
                                      {workload} active
                                    </span>
                                  )}
                                </span>
                                {visibleSources.length > 0 ? (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button
                                        type="button"
                                        onClick={(event) => event.stopPropagation()}
                                        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-slate-600 bg-slate-800 text-slate-400 hover:border-cyan-700 hover:text-cyan-300"
                                        aria-label={`Show Scenario Builder sources for ${candidate.user_name}`}
                                        title="Scenario Builder sources"
                                        data-testid={`candidate-sources-${card.id}-${role.role}-${candidate.user_id}`}
                                      >
                                        <Layers3 className="h-2.5 w-2.5" aria-hidden="true" />
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                      align="start"
                                      sideOffset={6}
                                      className="z-[80] w-fit max-w-[calc(100vw-2rem)] overflow-hidden border-slate-600 bg-slate-950 p-1.5 text-slate-100"
                                      onClick={(event) => event.stopPropagation()}
                                      data-testid={`candidate-sources-tooltip-${card.id}-${role.role}-${candidate.user_id}`}
                                    >
                                      <ScenarioCandidateSourceChips value={visibleSources} className="max-w-full flex-col items-stretch gap-1" />
                                    </PopoverContent>
                                  </Popover>
                                ) : isDirectOnly ? (
                                  <span
                                    className="inline-flex h-5 w-5 shrink-0 items-center justify-center text-slate-500"
                                    aria-label="Direct user candidate"
                                    title="Direct user candidate"
                                    data-testid={`candidate-direct-source-${card.id}-${role.role}-${candidate.user_id}`}
                                  >
                                    <UserRound className="h-3 w-3" aria-hidden="true" />
                                  </span>
                                ) : null}
                                <span className="ml-auto inline-flex shrink-0 flex-nowrap items-center justify-end gap-1.5 whitespace-nowrap">
                                  <PersonnelAvailabilitySummary
                                    presence={presenceInfoFor(candidate.user_id)}
                                    dutyInfo={candidateDuty}
                                    readinessInfo={readinessInfoFor(candidate)}
                                    readinessLoadState={readinessLoadState}
                                    variant="dark"
                                    align="end"
                                    testId={`candidate-availability-${card.id}-${role.role}-${candidate.user_id}`}
                                  />
                                </span>
                              </div>
                            );
                          })}
                          {!compactCandidates.length && <p className="px-2.5 py-2 text-[10px] italic text-slate-500">No saved recommendation for this role.</p>}
                        </div>
                      </>
                    )}
                  </div>
                ) : !attachedToTimeline ? (
                  <div className="mt-2 space-y-1.5">
                  {visibleCandidates.map((candidate, candidateIndex) => {
                    const isRecommended = recommendationIds.has(String(candidate.user_id));
                    const candidateReadiness = readinessInfoFor(candidate);
                    const warnings = warningsFor(candidate);
                    const key = `${card.id}:${role.role}:${candidate.user_id}`;
                    return (
                      <div key={candidate.user_id} className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-slate-700 bg-slate-900/80 px-2.5 py-2 text-[11px]">
                        <span className="font-semibold text-slate-100">{candidate.user_name}</span>
                        {isRecommended && <span className="rounded-full border border-violet-800 px-1.5 py-0.5 text-[9px] font-bold uppercase text-violet-300">Recommended</span>}
                        <PersonnelAvailabilitySummary
                          presence={presenceInfoFor(candidate.user_id)}
                          dutyInfo={dutyInfoFor(candidate.user_id)}
                          readinessInfo={candidateReadiness}
                          readinessLoadState={readinessLoadState}
                          variant="dark"
                          align="start"
                          testId={`quick-assign-availability-${card.id}-${role.role}-${candidate.user_id}`}
                        />
                        <button
                          type="button"
                          disabled={candidate.assigned || !role.gap || !!busyKey || !!confirmation}
                          onClick={() => requestAccept({ card_id: card.id, role: role.role, user_id: candidate.user_id }, key, `${candidate.user_name} assigned`, [candidate])}
                          className={`ml-auto rounded-md px-2.5 py-1 text-[10px] font-bold ${candidate.assigned ? 'bg-emerald-950 text-emerald-400' : warnings.length ? 'border border-amber-700 bg-amber-950/50 text-amber-300 hover:bg-amber-900' : 'bg-cyan-600 text-white hover:bg-cyan-500'} disabled:opacity-60`}
                        >
                          {candidate.assigned ? 'Assigned' : busyKey === key ? 'Assigning…' : warnings.length ? 'Assign anyway' : `Assign ${String(candidate.user_name || '').split(' ')[0]}`}
                        </button>
                      </div>
                    );
                  })}
                  {!visibleCandidates.length && <p className="text-[10px] italic text-slate-500">No candidates match the current availability filters.</p>}
                  {hiddenCandidates.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setExpandedCandidates((current) => ({ ...current, [expandKey]: !current[expandKey] }))}
                      className="inline-flex items-center gap-1 text-[10px] font-bold text-cyan-300 hover:text-cyan-200"
                      aria-expanded={!!expandedCandidates[expandKey]}
                      data-testid={`candidate-alternatives-${card.id}-${role.role}`}
                    >
                      <span>{expandedCandidates[expandKey] ? 'Hide alternatives' : `View ${hiddenCandidates.length} alternative${hiddenCandidates.length === 1 ? '' : 's'}`}</span>
                      <ChevronDown className={`h-3 w-3 shrink-0 transition-transform ${expandedCandidates[expandKey] ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  const body = (
    <div className={`space-y-3 ${embedded ? '' : 'flex-1 overflow-y-auto p-4'}`}>
      {error && <div className="rounded-lg border border-rose-800 bg-rose-950/40 px-3 py-2 text-xs text-rose-300">{error}</div>}
      {confirmation && (
        <section className="sticky top-0 z-20 rounded-xl border border-amber-700 bg-amber-950/95 p-3 shadow-xl shadow-black/40 backdrop-blur" data-testid="staffing-unsafe-confirmation">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold text-amber-200">Assign anyway?</p>
              <p className="mt-0.5 text-[10px] text-amber-300/80">This recommendation is not both on duty and ready for this incident.</p>
              <div className="mt-2 space-y-1">
                {confirmation.unsafe.map(({ candidate, warnings }) => <p key={candidate.user_id} className="text-[10px] text-slate-300"><strong className="text-white">{candidate.user_name}</strong> — {warnings.join(' ')}</p>)}
              </div>
              <div className="mt-3 flex justify-end gap-2">
                <button type="button" onClick={() => setConfirmation(null)} className="rounded-md border border-slate-600 px-3 py-1.5 text-[10px] font-bold text-slate-200 hover:bg-slate-800">Cancel</button>
                <button type="button" onClick={confirmAccept} className="rounded-md bg-amber-500 px-3 py-1.5 text-[10px] font-extrabold text-slate-950 hover:bg-amber-400" data-testid="staffing-confirm-unsafe">Assign anyway</button>
              </div>
            </div>
          </div>
        </section>
      )}

      {review.unavailable_candidate_count > 0 && <div className="rounded-lg border border-amber-800/70 bg-amber-950/30 px-3 py-2 text-[11px] text-amber-300">{review.unavailable_candidate_count} saved candidate{review.unavailable_candidate_count === 1 ? ' is' : 's are'} no longer available and {review.unavailable_candidate_count === 1 ? 'was' : 'were'} omitted.</div>}
      {loading && !review.cards.length && <p className="py-6 text-center text-sm text-slate-400">Loading staffing…</p>}

      {!embedded && staffingCards.length > 0 && (
        <PersonnelAvailabilityFilters
          value={availabilityFilters}
          onChange={setAvailabilityFilters}
          variant="dark"
          testIdPrefix={embedded ? 'inline-staffing-availability' : 'quick-assign-availability'}
          className="rounded-lg border border-slate-700 bg-slate-900/70 px-3 py-2"
        />
      )}

      {!embedded && staffingCards.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[10px] text-slate-400">Recommendations remain specific to each card. Repeated roles are not silently linked.</p>
          <button type="button" disabled={!safeRecommendations.length || !!busyKey || !!confirmation} onClick={applySafeRecommendations} className="inline-flex items-center gap-1.5 rounded-md border border-emerald-700 bg-emerald-950/50 px-3 py-1.5 text-[10px] font-bold text-emerald-300 hover:bg-emerald-900 disabled:cursor-not-allowed disabled:opacity-40" data-testid="apply-safe-recommendations">
            <CheckCircle2 className="h-3.5 w-3.5" /> {busyKey === 'safe-recommendations' ? 'Applying…' : `Apply safe recommendations${safeRecommendations.length ? ` (${safeRecommendations.length})` : ''}`}
          </button>
        </div>
      )}

      {embedded ? (
        <ExerciseTimelinePreview
          incident={incident}
          itemsOverride={items}
          variant="dark"
          compact
          renderInsideItem={(timelineItem) => {
            const card = (review.cards || []).find((entry) => String(entry.id) === String(timelineItem.id));
            if (!card) return null;
            const gap = (card.roles || []).reduce((sum, role) => sum + Number(role.gap || 0), 0);
            const content = renderStaffingCard(card, timelineItem, true);
            return content ? { gap, content } : null;
          }}
        />
      ) : staffingCards.map((card) => renderStaffingCard(card))}
    </div>
  );

  if (embedded) {
    return <div data-testid="inline-staffing-readiness">{body}</div>;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-[2px]" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-slate-700 bg-slate-900 shadow-2xl shadow-black/60" onClick={(event) => event.stopPropagation()} data-testid="quick-assign-modal">
        <div className="relative flex items-center justify-between border-b border-slate-700 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 px-5 py-4">
          <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-cyan-500 to-transparent" />
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-cyan-800 bg-cyan-950/80"><Users className="h-5 w-5 text-cyan-400" /></span>
            <div>
              <h3 className="text-sm font-extrabold uppercase tracking-[0.18em] text-slate-100">Staffing</h3>
              <p className="mt-0.5 text-[11px] text-slate-400">Review recommendations and fill role gaps.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider ${fullyStaffed ? 'border-emerald-700 bg-emerald-950/60 text-emerald-400' : 'border-amber-700 bg-amber-950/50 text-amber-400'}`}>
              {fullyStaffed ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
              {fullyStaffed ? 'Fully staffed' : `${review.gaps || 0} gap${Number(review.gaps || 0) === 1 ? '' : 's'}`}
            </span>
            <button type="button" onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-800 hover:text-white" aria-label="Close staffing"><X className="h-4 w-4" /></button>
          </div>
        </div>

        {body}

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-700 bg-slate-900 px-5 py-3">
          <p className="text-[10px] text-slate-400">Fill every current role slot, then activate from the response-plan controls.</p>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="rounded-md bg-slate-700 px-4 py-2 text-xs font-bold text-white hover:bg-slate-600">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}
